<template>
	<q-modal
		v-model="show_preview"
		:content-css="{ maxWidth: getMaxWidth, minWidth: getMinWidth, minHeight: '85vh' }"
		no-backdrop-dismiss
		no-esc-dismiss
	>
		<q-modal-layout>
			<q-toolbar slot="header">
				<q-btn flat round dense @click="show_preview = false" icon="fas fa-times" />
				<q-toolbar-title>
					{{ $t(game_name) }}
				</q-toolbar-title>
			</q-toolbar>

			<div class="layout-padding">
				<div class="q-pa-xs">
					<p class="text-bold">{{ $t('game_preview') }}</p>

					<div v-if="!game_finished">
						<!-- CAROUSEL -->
						<q-carousel
							v-model="slide"
							color="primary"
							:easing="overshoot"
							:autoplay="autoplay"
							class="q-my-sm"
							height="550px"
						>
							<!-- quick-nav -->
							<q-carousel-slide
								v-for="(card, index) in cards"
								:key="index"
								class="flex flex-center q-mb-lg bg-gray-1"
							>
								<!-- :class="`bg-${colors[n % 5]}`" -->
								<q-card class="card-style">
									<q-card-main>
										<div class="text-center">
											<div class="q-headline">
												{{ `${slide + 1}/${number_of_cards}` }}
											</div>
											<div class="image-container q-mt-sm">
												<div
													class="row justify-center foreground q-ml-md"
													:class="
														card.correct === true
															? 'correct'
															: card.correct === false
															? 'incorrect'
															: ''
													"
												>
													<div
														class="row items-end"
														v-if="card.correct === false"
													>
														<p class="q-headline tip">
															{{ `${$t('tip')}: ${card.tip}...` }}
														</p>
													</div>
												</div>
												<q-card-media class="q-ml-md">
													<img :src="card.image_link_signed" />
												</q-card-media>
											</div>

											<!-- <q-card-separator /> -->

											<div class="q-body-2 q-my-sm">
												{{ card.description }}
											</div>

											<div class="row q-my-sm q-ml-md">
												<div class="offset-1 col-8">
													<q-field>
														<q-input
															v-model="card.user_answer"
															:readonly="card.correct !== null"
															:float-label="$t('answer')"
														/>
													</q-field>
												</div>

												<div class="col-2">
													<q-btn
														v-if="card.correct === null"
														dense
														class="q-pa-md q-mt-sm q-ml-sm"
														color="amber"
														:disable="!card.user_answer.length"
														icon="far fa-paper-plane"
														@click="sendAnswer(index)"
													/>
													<q-btn
														v-else
														dense
														class="q-pa-xs q-mt-sm"
														:color="
															card.correct ? 'positive' : 'negative'
														"
														:label="
															card.correct
																? $t('correct')
																: $t('incorrect')
														"
														:icon="
															card.correct
																? 'far fa-thumbs-up'
																: 'far fa-thumbs-down'
														"
														@click="
															!card.correct
																? tryCardAgain(index)
																: null
														"
													/>
												</div>
											</div>
										</div>
									</q-card-main>
								</q-card>
							</q-carousel-slide>

							<q-carousel-control
								v-show="slide > 0"
								slot="control"
								position="bottom-left"
								:offset="[150, 160]"
								class="text-white"
								style="background: rgba(0, 0, 0, .3); padding: 4px; border-radius: 4px"
							>
								<q-btn
									flat
									color="primary"
									icon="fas fa-arrow-left"
									:disable="slide >= number_of_cards"
									@click="slide -= 1"
								/>
							</q-carousel-control>

							<q-carousel-control
								v-show="slide < number_of_cards - 1"
								slot="control"
								position="bottom-right"
								:offset="[150, 160]"
								class="text-white"
								style="background: rgba(0, 0, 0, .3); padding: 4px; border-radius: 4px"
							>
								<q-btn
									flat
									color="primary"
									icon="fas fa-arrow-right"
									:disable="slide >= cards.length"
									@click="slide += 1"
								/>
							</q-carousel-control>

							<q-carousel-control
								slot="control-progress"
								slot-scope="carousel"
								position="top"
								:offset="[260, 10]"
							>
								<q-progress
									:percentage="carousel.percentage"
									stripe
									color="primary"
									:animate="autoplay"
								/>
							</q-carousel-control>
						</q-carousel>
					</div>

					<div class="col-10 q-mt-lg" v-if="game_finished">
						<p class="text-bold q-mt-sm">{{ $t('result') }}</p>

						<q-slider
							disable
							markers
							snap
							v-model="user_score"
							:color="game_finished ? getSliderColor() : 'blue-6'"
							:min="0"
							:max="number_of_cards"
						/>

						<p class="q-mt-sm">
							{{ `${$t('correct_answers')}: ${user_score} / ${number_of_cards}` }}
						</p>
						<p v-if="user_won">{{ $t(success_message) }}</p>
						<p v-else>{{ $t(failure_message) }}</p>
					</div>
				</div>

				<div class="col-xs-12 q-mt-md">
					<div class="row justify-end">
						<q-btn color="secondary" :label="$t('back')" @click="resetGame" />
						<!-- <q-btn v-if="game_finished" color="primary"  class="q-ml-md" :label="$t('play_again')" @click="resetGame" /> -->
					</div>
				</div>
			</div>
		</q-modal-layout>
	</q-modal>
</template>

<script>
import { easing } from 'quasar'
import slugify from '../../../functions/slugify.js'

export default {
	name: 'AdminGameFlashcardsPreview',

	props: ['show_game_preview', 'game_name', 'images'],

	data() {
		return {
			slide: 0,
			autoplay: false,
			overshoot: easing.overshoot,
			cards: [],
			game_finished: false,
			number_of_cards: 0,
			correct_cards: 0,
			user_score: 0,
			user_won: false,
			success_message: `${this.game_name}_success_message`,
			failure_message: `${this.game_name}_failure_message`,
		}
	},

	computed: {
		show_preview: {
			get() {
				return this.show_game_preview
			},
			set() {
				this.$emit('hide_preview', '')
			},
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '100vw'
			else return '95vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '75vw'
		},
	},

	methods: {
		sendAnswer(idx) {
			let match = this.cards[idx]
			if (match) {
				slugify(match.user_answer.trim()) === match.slug
					? (match.correct = true)
					: (match.correct = false)

				setTimeout(() => {
					if (this.slide + 1 < this.cards.length) this.slide += 1
					if (this.cards.findIndex((e) => e.user_answer === '') === -1)
						this.checkResults()
				}, 1500)
			}
			return
		},

		tryCardAgain(idx) {
			let match = this.cards[idx]
			if (match) {
				match.user_answer = ''
				match.correct = null
			}
			return
		},

		getSliderColor() {
			let medium = Math.ceil(this.number_of_cards / 2)

			if (this.correct_cards < medium) return 'red-6'
			else if (this.correct_cards == medium) return 'blue-6'
			else return 'green-6'
		},

		checkResults() {
			this.game_finished = true

			let correctAnswers = this.cards.filter((e) => e.correct).length

			setTimeout(() => {
				this.correct_cards = correctAnswers
				this.user_score = correctAnswers
				if (this.correct_cards >= this.number_of_cards) this.user_won = true
			}, 750)
		},

		resetGame() {
			this.cards = []
			this.slide = 0
			this.number_of_cards = 0
			this.correct_cards = 0
			this.user_score = 0
			this.user_won = false
			this.game_finished = false
			this.show_preview = false
		},
	},

	mounted() {
		this.cards = this.images.map((e) => ({
			...e,
			tip: e.answer.substring(0, 3),
			correct: null,
			user_answer: '',
		}))
		this.number_of_cards = this.cards.length
	},
}
</script>

<style lang="stylus" scoped>
.image-container
    position relative
    img
      width 300px
      border-radius 0.6rem

.card-style
  width 360px

.foreground
    transition all 1.25s ease
    float left
    position absolute
    width 300px
    height 300px
    left 0
    top 0
    z-index 100
    font-weight bold
    border-radius 0.6rem

.incorrect
    background rgba(219, 40, 40, 0.5)

.correct
    background rgba(33, 186, 69, 0.5)

.tip
  color white
  padding 1rem
  background rgba(12, 12, 12, 0.5)
  border-radius 1rem
</style>
